import React, { useState, useEffect } from "react";
import axios from "axios";
import { Bar } from "react-chartjs-2";
import 'chart.js/auto';
import Swal from 'sweetalert2';
import axiosInstance from '../axiosInstance';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [recentReceipts, setRecentReceipts] = useState([]);
  const [events, setEvents] = useState([]);
  const [revenueChart, setRevenueChart] = useState(null);

  useEffect(() => {
    const fetchRevenue = async () => {
      try {
        const res = await axiosInstance.get('/dashboard/revenuechart');
        console.log("eenuse:", res)
        setRevenueChart({
          labels: res.data.labels || [],
          datasets: [
            {
              label: 'Monthly Revenue',
              data: res.data.data || [],
              backgroundColor: 'rgba(54, 162, 235, 0.6)'
            }
          ]
        });
      } catch (err) {
        console.error("Revenue chart error:", err);
        setRevenueChart({
          labels: [],
          datasets: [
            {
              label: 'Monthly Revenue',
              data: [],
              backgroundColor: 'rgba(54, 162, 235, 0.6)'
            }
          ]
        });
      }
    };

    fetchRevenue();
  }, []);
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await axiosInstance.get(`/dashboard/stats`);
        setDashboardData(res.data);
      } catch (err) {
        console.error("Error fetching dashboard:", err);
        setDashboardData(null); // fallback
      }
    };

    const fetchReceipts = async () => {
      try {
        const res = await axiosInstance.get(`/receipt/recent`);
        console.log("recents: ", res)
        setRecentReceipts(res.data.data?.receipts || []); // <-- use wrapped response
      } catch (err) {
        console.error("Error fetching receipts:", err);
        setRecentReceipts([]);
      }
    };

    const fetchEvents = async () => {
      try {
        const res = await axiosInstance.get(`/receipt/duedates`);
        console.log("events: ", res)
        const dueArray = res.data.data?.dueReceipts || [];
        setEvents(
          dueArray.map((r) => ({
            title: `Due: ₹${r.due_amt}`,
            date: r.due_date,
          }))
        );
      } catch (err) {
        console.error("Error fetching due dates:", err);
        setEvents([]);
      }
    };

    fetchDashboard();
    fetchReceipts();
    fetchEvents();
  }, []);


  const deleteReceipt = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "Do you really want to delete this receipt?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/receipt/hard/${id}`);
        setRecentReceipts(prev => prev.filter(r => r.id !== id));

        Swal.fire({
          title: 'Deleted!',
          text: 'The receipt has been deleted.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      } catch (error) {
        Swal.fire({
          title: 'Error!',
          text: 'There was a problem deleting the receipt.',
          icon: 'error'
        });
      }
    }
  };


  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Dashboard</h1>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          {!dashboardData ? (
            <div className="text-center mt-5">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-2">Loading dashboard data...</p>
            </div>
          ) : (
            <>
              {/*  Inventory & Loan Overview */}
              <h4 className="mb-3"> Inventory & Loan Overview</h4>
              <div className="row">
                <StatBox color="warning" icon="fas fa-warehouse" label="Current Stock" value={dashboardData.active_dealer_stock} link="/stocks" />
                <StatBox color="success" icon="fas fa-handshake" label="Active Loans" value={dashboardData.active_finance} link="/finance" />
                <StatBox color="secondary" icon="fas fa-check-circle" label="Closed Loans" value={dashboardData.closed_finance} link="/finance" />
                <StatBox color="indigo" icon="fas fa-exchange-alt" label="Exchange Bikes" value={dashboardData.bikes_exchanged} link="/exchange" />
                <StatBox color="danger" icon="fas fa-motorcycle" label="Seized Bikes" value={dashboardData.bike_seizzed} link="/seizedvehicle" />
              </div>

              {/* Today’s Summary */}
              <h4 className="mt-4 mb-3"> Today’s Summary</h4>
              <div className="row">
                <StatBox color="maroon" icon="fas fa-clock" label="Today's Due" value={dashboardData.todays_due} />
                <StatBox color="primary" icon="fas fa-receipt" label="Today's Receipts" value={dashboardData.today_receipts} />
                <StatBox color="teal" icon="fas fa-coins" label="Today's Collection" value={`₹${(+dashboardData.today_collection).toLocaleString('en-IN')}`} />
              </div>

              {/*  Monthly & Annual Overview */}
              <h4 className="mt-4 mb-3"> Monthly & Annual Overview</h4>
              <div className="row">
                <StatBox color="info" icon="fas fa-file-invoice-dollar" label="Monthly Receipts" value={dashboardData.monthly_receipts} />
                <StatBox color="olive" icon="fas fa-wallet" label="Monthly Collection" value={`₹${(+dashboardData.monthly_collection).toLocaleString('en-IN')}`} />
                <StatBox color="cyan" icon="fas fa-calendar-alt" label="Monthly Sales" value={dashboardData.monthly_sales} />
                <StatBox color="purple" icon="fas fa-rupee-sign" label="Monthly Revenue" value={`₹${(+dashboardData.monthly_revenue).toLocaleString('en-IN')}`} />
                <StatBox color="primary" icon="fas fa-chart-line" label="Annual Sales" value={dashboardData.yearly_sales} />
              </div>
            </>
          )}

          {/* Chart */}
          <div className="row">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header bg-gradient-dark text-white">Monthly Revenue Chart</div>
                <div className="card-body">
                  {revenueChart ? <Bar data={revenueChart} /> : <p>Loading chart...</p>}
                </div>
              </div>
            </div>
          </div>


          {/* Recent Table */}
          <div className="card mt-4">
            <div className="card-header bg-success text-white">Recent Receipts</div>
            <div className="card-body table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Reg No</th>
                    <th>Name</th>
                    <th>Receipt No</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {recentReceipts.map((r) => (
                    <tr key={r.id}>
                      <td>{r.reg_no}</td>
                      <td>{r.recipient_name}</td>
                      <td>{r.id}</td>
                      <td>₹{r.total}</td>
                      <td>{new Date(r.createdAt).toLocaleDateString("en-GB")}</td>
                      <td><button className="btn btn-sm btn-danger" onClick={() => deleteReceipt(r.id)}>Delete</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

// 🔁 Reusable Stat Box Component
const StatBox = ({ color, icon, label, value, link }) => (
  <div className="col-lg-3 col-md-6 col-sm-6 col-12">
    <div className={`small-box bg-${color}`}>
      <div className="inner">
        <h3>{value}</h3>
        <p>{label}</p>
      </div>
      <div className="icon">
        <i className={icon}></i>
      </div>
      {link && (
        <a href={link} className="small-box-footer">
          More info <i className="fas fa-arrow-circle-right"></i>
        </a>
      )}
    </div>
  </div>
);



export default Dashboard;
